/**
 * Battle panel
 *
 * @author Guangcong Luo <guangcongluo@gmail.com>
 * @license AGPLv3
 */

import preact from "../js/lib/preact";
import {
	PS, PSRoom, type RoomOptions, type RoomID, Config, type BattlePanelLayout,
} from "./client-main";
import { PSIcon, PSPanelWrapper, PSRoomPanel } from "./panels";
import { ChatLog, ChatRoom, ChatTextEntry, ChatUserList } from "./panel-chat";
import { FormatDropdown } from "./panel-mainmenu";
import { Battle, type Pokemon, type ServerPokemon } from "./battle";
import { BattleScene } from "./battle-animations";
import { Dex, toID, type ID } from "./battle-dex";
import {
	BattleChoiceBuilder, type BattleRequestActivePokemon, type BattleRequestSideInfo,
	type BattleRequest, type BattleMoveRequest, type BattleSwitchRequest, type BattleTeamRequest,
} from "./battle-choices";
import type { Args } from "./battle-text-parser";
import { ModifiableValue } from "./battle-tooltips";
import { Net } from "./client-connection";
import { BattleLog } from "./battle-log";
import { getClientLanguage, trUI } from "./client-i18n";

/** Refcount: forzar un panel al espectar sin persistir la preferencia. */
let etSpectatorOnepanelDepth = 0;
let etSavedOnepanel: boolean | 'vertical' | null = null;

type BattleDesc = {
	id: RoomID,
	minElo?: number | string,
	p1?: string,
	p2?: string,
	p3?: string,
	p4?: string,
};
export class BattlesRoom extends PSRoom {
	override readonly classType = 'battles';
	/** null means still loading */
	format = '';
	filters = '';
	battles: BattleDesc[] | null = null;
	constructor(options: RoomOptions) {
		super(options);
		this.refresh();
		// If graphics preference is set to use BW sprites
		if (PS.prefs.bwgfx) {
			Dex.loadSpriteData('bw');
		}
	}
	setFormat(format: string) {
		if (format === this.format) return this.refresh();
		this.battles = null;
		this.format = format;
		this.update(null);
		this.refresh();
	}
	refresh() {
		PS.send(`/cmd roomlist ${toID(this.format)}, ${this.filters}`);
	}
}

class BattlesPanel extends PSRoomPanel<BattlesRoom> {
	static readonly id = 'battles';
	static readonly routes = ['battles'];
	static readonly Model = BattlesRoom;
	static readonly location = 'right';
	static readonly icon = <i class="fa fa-caret-square-o-right" aria-hidden></i>;
	static readonly title = 'Battles';
	refresh = () => {
		this.props.room.refresh();
	};
	changeFormat = (e: Event) => {
		const value = (e.target as HTMLButtonElement).value;
		this.props.room.setFormat(value);
	};
	applyFilters = (e: Event) => {
		e.preventDefault();
		const minElo = this.base?.querySelector<HTMLInputElement>(`select[name=elofilter]`)?.value;
		const searchPrefix = this.base?.querySelector<HTMLInputElement>(`input[name=prefixsearch]`)?.value;
		this.props.room.filters = `${minElo || ''},${searchPrefix || ''}`;
		this.refresh();
	};
	renderBattleLink(battle: BattleDesc) {
		const format = battle.id.split('-')[1];
		const minEloMessage = typeof battle.minElo === 'number' ? `rated ${battle.minElo}` : battle.minElo;
		return <div key={battle.id}><a href={`/${battle.id}`} class="blocklink">
			{minEloMessage && <small style="float:right">({minEloMessage})</small>}
			<small>[{format}]</small><br />
			<em class="p1">{battle.p1}</em> <small class="vs">vs.</small> <em class="p2">{battle.p2}</em>
		</a></div>;
	}
	override render() {
		const room = this.props.room;
		return <PSPanelWrapper room={room}><div class="pad">
			<button class="button" style="float:right;font-size:10pt;margin-top:3px" name="closeRoom">
				<i class="fa fa-times" aria-hidden></i> {trUI("Close")}
			</button>
			<div class="roomlist">
				<p>
					<button class="button" name="refresh" onClick={this.refresh}>
						<i class="fa fa-refresh" aria-hidden></i> {trUI("Refresh")}
					</button> {}
					<span
						style={Dex.getPokemonIcon('meloetta-pirouette') + ';display:inline-block;vertical-align:middle'} class="picon"
						title="Meloetta is PS's mascot! The Pirouette forme is Fighting-type, and represents our battles."
					></span>
				</p>

				<p>
					<label class="label">{trUI("Format:")}</label><FormatDropdown onChange={this.changeFormat} placeholder={trUI("(All formats)")} />
				</p>
				<label class="label">
					{trUI("Minimum Elo:")} <select name="elofilter" class="select" onChange={this.applyFilters}>
						<option value="none">{trUI("None")}</option><option value="1100">1100</option><option value="1300">1300</option>
						<option value="1500">1500</option><option value="1700">1700</option><option value="1900">1900</option>
					</select>
				</label>

				<form class="search" onSubmit={this.applyFilters}>
					<p>
						<input type="text" name="prefixsearch" class="textbox" placeholder={trUI("Username prefix")} autocomplete="off" />
						<button type="submit" class="button">{trUI("Search")}</button>
					</p>
				</form>
				<div class="list">{!room.battles ? (
					<p>{trUI("Loading...")}</p>
				) : !room.battles.length ? (
					<p>{trUI("No battles are going on")}</p>
				) : (<>
					<p>{room.battles.length === 100 ?
						`100+` : room.battles.length} {room.battles.length > 1 ? `battles` : `battle`}</p>
					{room.battles.map(battle => this.renderBattleLink(battle))}
				</>
				)}</div>
			</div>
		</div></PSPanelWrapper>;
	}
}

export class BattleRoom extends ChatRoom {
	override readonly classType = 'battle';
	declare pmTarget: null;
	declare challengeMenuOpen: false;
	declare challengingFormat: null;
	declare challengedFormat: null;

	override battle: Battle = null!;
	/** null if spectator, otherwise current player's info */
	side: BattleRequestSideInfo | null = null;
	request: BattleRequest | null = null;
	choices: BattleChoiceBuilder | null = null;
	autoTimerActivated: boolean | null = null;
	requireForfeit = false;
	/** should be false if we joined right after accepting or challenging a battle,
	  * and true if we refreshed and rejoined a battle.
		* null = initializing, we don't know yet */
	rejoining: boolean | null = null;
	overlayActive: 'move' | 'switch' | null = null;

	override interruptClose(explicit?: boolean, elem?: HTMLElement | null) {
		if (this.isPlaying() || this.requireForfeit) {
			PS.join('forfeitbattle' as RoomID, { parentElem: elem, parentRoomid: this.id });
			return `You are still in ${this.title}`;
		}
		return super.interruptClose(explicit, elem);
	}
	isPlaying() {
		return this.battle && !this.battle.ended && this.request && this.connectMode !== 'deleted';
	}
	updateChoiceNotification() {
		let oName = this.battle?.farSide.name;
		if (oName) oName = " against " + oName;
		let title = '';
		let body = '';
		switch (this.request?.requestType) {
		case 'move':
			title = trUI("Your move!");
			body = trUI("Move in your battle") + oName;
			break;
		case 'switch':
			title = trUI("Your switch!");
			body = trUI("Switch in your battle") + oName;
			break;
		case 'team':
			title = trUI("Team preview!");
			body = trUI("Choose your team order in your battle") + oName;
			break;
		}

		if (!this.choices || this.choices.isDone()) body = '';

		const current = this.notifications.find(notification => notification.id === 'choice');
		if ((current?.body || '') === body) return;

		if (!body) {
			this.dismissNotification('choice');
		} else {
			this.notify({ title, body, id: 'choice', noAutoDismiss: true });
		}
	}

	override handleReconnect(): boolean | void {
		if (this.battle) {
			this.battle.stepQueue = [];
			this.battle.preemptStepQueue = [];
			this.battle.resetStep();
		}
		this.side = null;
		this.request = null;
		this.choices = null;
		this.updateChoiceNotification();
		return false;
	}

	override destroy() {
		this.request = null;
		this.choices = null;
		super.destroy();
	}

	loadReplay() {
		const replayid = this.id.slice(7);
		Net(`https://replay.pokemonshowdown.com/${replayid}.json`).get().catch(() => '').then(data => {
			try {
				const replay = JSON.parse(data);
				this.title = `[${replay.format}] ${replay.players.join(' vs. ')}`;
				this.battle.stepQueue = replay.log.split('\n');
				this.battle.atQueueEnd = false;
				this.battle.pause();
				this.battle.seekTurn(0);
				this.connectMode = null;
				this.connectError = null;
				this.update(null);
			} catch {
				this.connectError = `Battle "${replayid}" not found`;
				if (!this.battle.stepQueue.length) {
					this.battle.scene.message(
						`<div class="broadcast-red pad"><strong>${BattleLog.escapeHTML(this.connectError)}</strong></div><br />` +
						`The battle you're looking for has expired. Battles expire after 15 minutes of inactivity unless they're saved.<br /><br />` +
						`In the future, remember to click "Save replay" to save a replay permanently.`
					);
				}
				this.update(null);
			}
		});
	}
}

class BattleDiv extends preact.Component<{ room: BattleRoom }> {
	override shouldComponentUpdate() {
		return false;
	}
	override componentDidMount() {
		const room = this.props.room;
		if (room.battle) {
			this.base!.replaceChild(room.battle.scene.$frame![0], this.base!.firstChild!);
		}
	}
	override render() {
		return <div><div class="battle"></div></div>;
	}
}

class TimerButton extends preact.Component<{ room: BattleRoom, top: number }> {
	timerInterval: number | null = null;
	override componentWillUnmount() {
		if (this.timerInterval) {
			clearInterval(this.timerInterval);
			this.timerInterval = null;
		}
	}
	secondsToTime(seconds: number | true) {
		if (seconds === true) return '-:--';
		const minutes = Math.floor(seconds / 60);
		seconds -= minutes * 60;
		return `${minutes}:${(seconds < 10 ? '0' : '')}${seconds}`;
	}
	render() {
		let time = trUI("Timer");
		const room = this.props.room;
		if (!this.timerInterval && room.battle.kickingInactive) {
			this.timerInterval = setInterval(() => {
				if (room.choices?.isDone()) return;
				if (typeof room.battle.kickingInactive === 'number' && room.battle.kickingInactive > 1) {
					room.battle.kickingInactive--;
					if (room.battle.graceTimeLeft) room.battle.graceTimeLeft--;
					else if (room.battle.totalTimeLeft) room.battle.totalTimeLeft--;
				}
				this.forceUpdate();
			}, 1000);
		} else if (this.timerInterval && !room.battle.kickingInactive) {
			clearInterval(this.timerInterval);
			this.timerInterval = null;
		}

		let timerTicking = (room.battle.kickingInactive &&
			room.request && room.request.requestType !== "wait" && (room.choices && !room.choices.isDone())) ?
			' timerbutton-on' : '';

		if (room.battle.kickingInactive) {
			const secondsLeft = room.battle.kickingInactive;
			time = this.secondsToTime(secondsLeft);
			if (secondsLeft !== true) {
				if (secondsLeft <= 10 && timerTicking) {
					timerTicking = ' timerbutton-critical';
				}

				if (room.battle.totalTimeLeft) {
					const totalTime = this.secondsToTime(room.battle.totalTimeLeft);
					time += ` |  ${totalTime} ${trUI("total")}`;
				}
			}
		}

		return <button
			style={{ position: "absolute", right: '10px', top: `${this.props.top}px` }}
			data-href="battletimer" class={`button${timerTicking}`} role="timer"
		>
			<i class="fa fa-hourglass-start" aria-hidden></i> {time}
		</button>;
	}
};

class BattlePanel extends PSRoomPanel<BattleRoom> {
	static readonly id = 'battle';
	static readonly routes = ['battle-*', 'game-*'];
	static readonly Model = BattleRoom;
	static handleDrop(ev: DragEvent) {
		const file = ev.dataTransfer?.files?.[0];
		if (file?.type === 'text/html') {
			let roomNum = 1;
			for (; roomNum < 100; roomNum++) {
				if (!PS.rooms[`battle-uploaded-${roomNum}`]) break;
			}
			file.text().then(html => {
				const titleStart = html.indexOf('<title>');
				const titleEnd = html.indexOf('</title>');
				let title = 'Uploaded Replay';
				if (titleStart >= 0 && titleEnd > titleStart) {
					title = html.slice(titleStart + 7, titleEnd - 1);
					const colonIndex = title.indexOf(':');
					const hyphenIndex = title.lastIndexOf('-');
					if (hyphenIndex > colonIndex + 2) {
						title = title.substring(colonIndex + 2, hyphenIndex - 1);
					} else {
						title = title.substring(colonIndex + 2);
					}
				}
				const index1 = html.indexOf('<script type="text/plain" class="battle-log-data">');
				const index2 = html.indexOf('<script type="text/plain" class="log">');
				if (index1 < 0 && index2 < 0) {
					PS.alert("Unrecognized HTML file: Only replay files are supported.");
					return;
				}
				if (index1 >= 0) {
					html = html.slice(index1 + 50);
				} else if (index2 >= 0) {
					html = html.slice(index2 + 38);
				}
				const index3 = html.indexOf('</script>');
				html = html.slice(0, index3);
				html = html.replace(/\\\//g, '/');

				PS.join(`battle-uploaded-${roomNum}` as RoomID);
				const room = PS.rooms[`battle-uploaded-${roomNum}`] as BattleRoom;
				if (!room) return;

				room.title = title;
				room.connectMode = null;
				PS.receive(`>battle-uploaded-${roomNum}\n${html}`);
			});
			return true;
		}
	}
	/** last displayed team. will not show the most recent request until the last one is gone. */
	team: ServerPokemon[] | null = null;
	mobileChatShown = false;
	showMobileChat = () => {
		this.mobileChatShown = true;
		this.forceUpdate();
	};
	showMobileBattle = () => {
		this.mobileChatShown = false;
		this.forceUpdate();
	};
	send = (text: string, elem?: HTMLElement) => {
		this.props.room.send(text, elem);
	};
	focusIfNoSelection = () => {
		if (window.getSelection?.()?.type === 'Range') return;
		this.focus();
	};
	onKey = (e: KeyboardEvent) => {
		if (e.keyCode === 33) { // Pg Up key
			const chatLog = this.base!.getElementsByClassName('chat-log')[0] as HTMLDivElement;
			chatLog.scrollTop = chatLog.scrollTop - chatLog.offsetHeight + 60;
			return true;
		} else if (e.keyCode === 34) { // Pg Dn key
			const chatLog = this.base!.getElementsByClassName('chat-log')[0] as HTMLDivElement;
			chatLog.scrollTop = chatLog.scrollTop + chatLog.offsetHeight - 60;
			return true;
		}
		return false;
	};
	/**
	 * Pill styles hide the checkbox. Drive toggle from the label click so it works even when
	 * native label→checkbox activation fails (Opera GX / zero-size inputs with pointer-events quirks).
	 */
	toggleBoostedMoveLabel = (e: Event) => {
		const label = e.currentTarget as HTMLLabelElement;
		const checkbox = label.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
		if (!checkbox) return;
		e.preventDefault();
		this.applyBoostedMove(checkbox.name, !checkbox.checked);
	};
	applyBoostedMove(name: string, checked: boolean) {
		const choices = this.props.room.choices;
		if (!choices) return;
		switch (name) {
		case 'mega':
			choices.current.mega = checked;
			break;
		case 'megax':
			choices.current.megax = checked;
			choices.current.megay = false;
			break;
		case 'megay':
			choices.current.megay = checked;
			choices.current.megax = false;
			break;
		case 'ultra':
			choices.current.ultra = checked;
			break;
		case 'z':
			choices.current.z = checked;
			break;
		case 'max':
			choices.current.max = checked;
			break;
		case 'tera':
			choices.current.tera = checked;
			break;
		}
		this.props.room.update(null);
	};
	override componentDidMount() {
		const room = this.props.room;
		const $elem = $(this.base!);
		const battle = (room.battle ||= new Battle({
			id: room.id as any,
			$frame: $elem.find('.battle'),
			$logFrame: $elem.find('.battle-log'),
			log: room.backlog?.map(args => '|' + args.join('|')),
		}));
		const scene = battle.scene as BattleScene;
		room.backlog = null;
		room.log ||= scene.log;
		room.log.getHighlight = room.handleHighlight;
		scene.tooltips.unlisten(scene.$frame);
		scene.tooltips.listen(this.base!);
		super.componentDidMount();
		// Idioma / settings: re-renderizar menú de movimientos y textos UI al vuelo
		this.subscriptions.push(PS.prefs.subscribe(key => {
			if (key === 'serversettings' || key === null) this.forceUpdate();
		}));
		if (!PS.prefs.spectatefromstart) battle.seekTurn(Infinity);
		if (PS.prefs.autohardcore) {
			battle.setHardcoreMode(true);
		}
		// Jugador: coalescer playing/paused/atqueueend a 1×/frame (animaciones).
		// callback/turn/ended: forceUpdate inmediato (menú de movimientos / fin).
		battle.subscribe(state => this.onBattleSubscription(state));
		this.etWasSpectator = !room.side;
		this.syncSpectatorModeBindings(true);
	}
	override componentDidUpdate() {
		const isSpec = !this.props.room.side;
		// Transición espectador ↔ jugador: rebind estricto (no leave listeners/measure en player)
		if (isSpec !== this.etWasSpectator) {
			this.etWasSpectator = isSpec;
			this.syncSpectatorModeBindings(true);
			return;
		}
		if (!isSpec) return;
		this.syncSpectatorImmersiveLayout();
		// No medir en cada forceUpdate de batalla — solo si cambió layout / side / tamaño
		const key = this.spectatorLayoutKey();
		if (key !== this.etSpecLayoutKey) {
			this.etSpecLayoutKey = key;
			this.scheduleSpectatorComposeWidth();
		}
	}
	override componentWillUnmount() {
		this.bindSpectatorComposeSync(false);
		this.releaseSpectatorOnepanel();
		if (this.etPlayerFuRaf) {
			cancelAnimationFrame(this.etPlayerFuRaf);
			this.etPlayerFuRaf = 0;
		}
		const battle = this.props.room.battle;
		if (battle) battle.subscription = null;
		const scene = battle?.scene as BattleScene | undefined;
		if (this.base) scene?.tooltips.unlisten(this.base);
		super.componentWillUnmount();
	}
	/** true si este panel forzó un panel temporalmente */
	private etForcedOnepanel = false;
	private etWasSpectator = false;
	private etPlayerFuRaf = 0;
	private etSpecComposeRO: ResizeObserver | null = null;
	private etSpecComposeRaf = 0;
	private etSpecComposePending = false;
	private etSpecComposeLastW = -1;
	private etSpecComposeSettle = false;
	private etSpecLayoutKey = '';
	private etSpecComposeBound = () => this.scheduleSpectatorComposeWidth();
	private etSpecComposeFullscreenBound = () => this.scheduleSpectatorComposeWidth(true);
	private etSpecComposeListening = false;
	/** Bind spectator-only work; no-op for players except releasing prior spectator state. */
	syncSpectatorModeBindings(settleCompose = false) {
		const isSpec = !this.props.room.side;
		if (isSpec) {
			this.bindSpectatorComposeSync(true);
			this.syncSpectatorImmersiveLayout();
			this.scheduleSpectatorComposeWidth(settleCompose);
		} else {
			this.bindSpectatorComposeSync(false);
			this.releaseSpectatorOnepanel();
		}
		this.etSpecLayoutKey = this.spectatorLayoutKey();
	}
	onBattleSubscription(
		state: 'playing' | 'paused' | 'turn' | 'atqueueend' | 'callback' | 'ended' | 'error'
	) {
		const isPlayer = !!this.props.room.side;
		if (isPlayer && (state === 'playing' || state === 'paused' || state === 'atqueueend')) {
			this.schedulePlayerForceUpdate();
			return;
		}
		if (this.etPlayerFuRaf) {
			cancelAnimationFrame(this.etPlayerFuRaf);
			this.etPlayerFuRaf = 0;
		}
		this.forceUpdate();
	}
	/** Coalesce animation-driven panel updates to one Preact render per frame (player only). */
	schedulePlayerForceUpdate() {
		if (this.etPlayerFuRaf) return;
		this.etPlayerFuRaf = requestAnimationFrame(() => {
			this.etPlayerFuRaf = 0;
			this.forceUpdate();
		});
	}
	private spectatorLayoutKey() {
		const room = this.props.room;
		return `${room.side ? 1 : 0}|${room.width}|${room.height}|${PS.prefs.onepanel ? 1 : 0}`;
	}
	/** Bind/unbind resize + fullscreen listeners for spectator compose clipping. */
	bindSpectatorComposeSync(on: boolean) {
		// Strict gate: never attach measure/listeners while playing as a player
		if (on && this.props.room.side) return;
		if (on) {
			if (!this.etSpecComposeListening) {
				this.etSpecComposeListening = true;
				window.addEventListener('resize', this.etSpecComposeBound);
				document.addEventListener('fullscreenchange', this.etSpecComposeFullscreenBound);
				window.visualViewport?.addEventListener('resize', this.etSpecComposeBound);
			}
			// Observar solo el log (no todo el room): animaciones de batalla no deben forzar measure
			if (typeof ResizeObserver !== 'undefined' && this.base && !this.etSpecComposeRO) {
				const log = (this.base as HTMLElement).querySelector(':scope > .battle-log');
				this.etSpecComposeRO = new ResizeObserver(() => this.scheduleSpectatorComposeWidth());
				if (log) this.etSpecComposeRO.observe(log);
				else this.etSpecComposeRO.observe(this.base as Element);
			}
		} else {
			if (this.etSpecComposeListening) {
				this.etSpecComposeListening = false;
				window.removeEventListener('resize', this.etSpecComposeBound);
				document.removeEventListener('fullscreenchange', this.etSpecComposeFullscreenBound);
				window.visualViewport?.removeEventListener('resize', this.etSpecComposeBound);
			}
			this.etSpecComposeRO?.disconnect();
			this.etSpecComposeRO = null;
			this.etSpecComposePending = false;
			this.etSpecComposeSettle = false;
			if (this.etSpecComposeRaf) {
				cancelAnimationFrame(this.etSpecComposeRaf);
				this.etSpecComposeRaf = 0;
			}
		}
	}
	/**
	 * Schedule compose measure at most once per frame. Avoids layout thrashing from
	 * battle.subscribe → forceUpdate → componentDidUpdate during animations.
	 */
	scheduleSpectatorComposeWidth(settlePass = false) {
		if (document.hidden) return;
		// Player battles never need compose clipping
		if (this.props.room.side) return;
		if (settlePass) this.etSpecComposeSettle = true;
		if (this.etSpecComposePending) return;
		this.etSpecComposePending = true;
		if (this.etSpecComposeRaf) cancelAnimationFrame(this.etSpecComposeRaf);
		this.etSpecComposeRaf = requestAnimationFrame(() => {
			this.etSpecComposeRaf = 0;
			this.etSpecComposePending = false;
			const needSettle = this.etSpecComposeSettle;
			this.etSpecComposeSettle = false;
			this.syncSpectatorComposeWidthNow();
			// Un solo segundo pase tras fullscreen / mount (scrollbar / F11)
			if (needSettle) {
				this.etSpecComposeRaf = requestAnimationFrame(() => {
					this.etSpecComposeRaf = 0;
					this.syncSpectatorComposeWidthNow();
				});
			}
		});
	}
	/**
	 * Clip spectator compose to the real left edge of the right log panel.
	 * Uses getBoundingClientRect so F11 / scrollbar / viewport quirks stay accurate.
	 * Only writes CSS when width changes by ≥1px.
	 */
	syncSpectatorComposeWidthNow() {
		const room = this.props.room;
		const root = this.base as HTMLElement | null;
		if (!root) return;
		if (room.side) {
			if (this.etSpecComposeLastW !== 0) {
				this.etSpecComposeLastW = 0;
				root.style.removeProperty('--et-spec-chat-w');
				const compose = root.querySelector<HTMLElement>(':scope > .chat-log-add.et-spec-compose');
				compose?.style.removeProperty('right');
				compose?.style.removeProperty('left');
			}
			return;
		}
		const log = root.querySelector<HTMLElement>(':scope > .battle-log');
		if (!log) return;
		const rootRect = root.getBoundingClientRect();
		const logRect = log.getBoundingClientRect();
		// Gap from room's right edge to the log's left edge (= width reserved on the right)
		// +2px safety so F11 never bleeds under the panel
		const rightGap = Math.max(0, Math.round(rootRect.right - logRect.left) + 2);
		if (Math.abs(rightGap - this.etSpecComposeLastW) < 1) return;
		this.etSpecComposeLastW = rightGap;
		root.style.setProperty('--et-spec-chat-w', `${rightGap}px`);
		const compose = root.querySelector<HTMLElement>(':scope > .chat-log-add.et-spec-compose');
		if (compose) {
			compose.style.setProperty('right', `${rightGap}px`, 'important');
			// Leave room for the left userlist rail inside the combat column
			compose.style.setProperty('left', '146px', 'important');
		}
	}
	/** @deprecated use scheduleSpectatorComposeWidth — kept as alias for call sites */
	syncSpectatorComposeWidth() {
		this.scheduleSpectatorComposeWidth();
	}
	/** Espectador + dos paneles → un panel inmersivo (sin guardar prefs). */
	syncSpectatorImmersiveLayout() {
		const room = this.props.room;
		const focused = PS.room === room || PS.panel === room;
		const want = !room.side && focused;
		if (want) this.applySpectatorOnepanel();
		else this.releaseSpectatorOnepanel();
	}
	private applySpectatorOnepanel() {
		if (this.etForcedOnepanel) return;
		// Ya está en un panel / vertical: no tocar
		if (PS.prefs.onepanel) return;
		if (etSpectatorOnepanelDepth === 0) {
			etSavedOnepanel = PS.prefs.onepanel;
			PS.prefs.onepanel = true;
		}
		etSpectatorOnepanelDepth++;
		this.etForcedOnepanel = true;
		PS.update();
	}
	private releaseSpectatorOnepanel() {
		if (!this.etForcedOnepanel) return;
		this.etForcedOnepanel = false;
		etSpectatorOnepanelDepth = Math.max(0, etSpectatorOnepanelDepth - 1);
		if (etSpectatorOnepanelDepth === 0 && etSavedOnepanel !== null) {
			const restore = etSavedOnepanel;
			etSavedOnepanel = null;
			// Solo deshacer nuestro override temporal
			if (PS.prefs.onepanel === true) {
				PS.prefs.onepanel = restore;
			}
			PS.update();
		}
	}
	battleHeight = 360;
	/** Última altura aplicada al frame (evita animReset en cada re-render). */
	private appliedBattleHeight: number | null = null;
	updateLayout() {
		if (!this.base) return;
		const room = this.props.room;
		if (!room.width) return;
		const { battleHeight } = this.chooseLayout();
		this.battleHeight = battleHeight;
		const $frame = room.battle?.scene.$frame;
		if (!$frame) return;
		// Evitar $frame.css en cada forceUpdate (turno/anim) — solo si cambia escala
		const scaleChanged = this.appliedBattleHeight !== battleHeight;
		if (!scaleChanged) return;
		this.appliedBattleHeight = battleHeight;
		if (battleHeight !== 360) {
			$frame.css({
				transform: `scale(${battleHeight / 360})`,
				transformOrigin: 'top left',
				webkitTransformOrigin: 'top left',
			});
		} else {
			$frame.css({ transform: 'none', transformOrigin: '', webkitTransformOrigin: '' });
		}
		// Solo reaplicar A/B cuando cambia la escala. animReset en cada render
		// (cada mensaje de combate) corta la animación de cambio de Pokémon.
		if (!room.side && room.battle) {
			for (const side of room.battle.sides) {
				for (const poke of side.active) {
					if (poke?.sprite && !poke.fainted) poke.sprite.animReset();
				}
			}
			// Scale change can shift log edge — remeasure once
			this.scheduleSpectatorComposeWidth();
		}
	}
	/**
	 * Spectator cinema: left rail userlist + arena/controls/compose,
	 * full battle log + chat history as the right panel.
	 */
	chooseSpectatorCinemaLayout(): {
		layout: BattlePanelLayout,
		battleHeight: number,
		battleWidth: number,
		overlayControls: boolean,
	} {
		const room = this.props.room;
		const width = Math.max(320, room.width || 640);
		const height = Math.max(280, room.height || 480);
		const chatWidth = this.spectatorChatWidth();
		const userListWidth = 146;
		const vsReserve = 110;
		const controlsReserve = 118;
		const inputReserve = 48;
		const stagePad = 20;
		const availW = Math.max(300, width - chatWidth - userListWidth - 8);
		const availH = Math.max(200, height - vsReserve - controlsReserve - inputReserve - stagePad);
		const maxScale = 1.34;
		const scale = Math.min(maxScale, availW / 640, availH / 360);
		const battleHeight = Math.round(360 * scale);
		const battleWidth = Math.round(640 * scale);
		return {
			layout: 'side-by-side',
			battleHeight,
			battleWidth,
			overlayControls: false,
		};
	}
	/** Ancho del panel derecho de log/chat en modo espectador. */
	spectatorChatWidth(): number {
		const width = Math.max(320, this.props.room.width || 640);
		return Math.min(380, Math.max(300, Math.round(width * 0.28)));
	}
	/** Reserva mínima de chat al escalar la batalla (combatiente). */
	playerChatWidth(): number {
		const width = Math.max(320, this.props.room.width || 640);
		// Antes: ~20% / 220–280px (dejaba gutters grandes). Ahora ~28% / 280–360px.
		return Math.min(360, Math.max(280, Math.round(width * 0.28)));
	}
	/**
	 * Chat final combatiente: pega a la derecha y ocupa el hueco sobrante
	 * tras alinear la batalla a la izquierda (sin centrar en la columna).
	 */
	playerChatFillWidth(battleWidth: number, stagePadX = 8, gap = 8): number {
		const width = Math.max(320, this.props.room.width || 640);
		const minChat = this.playerChatWidth();
		const fill = width - battleWidth - stagePadX - gap;
		return Math.max(minChat, fill);
	}
	chooseLayout(): {
		layout: BattlePanelLayout,
		battleHeight: number,
		battleWidth: number,
		overlayControls: boolean,
	} {
		const room = this.props.room;
		// Spectator immersive layout is separate from player battle layout.
		if (!room.side) return this.chooseSpectatorCinemaLayout();
		return PS.chooseBattleLayout(room.width, room.height, PS.prefs.battlelayout);
	}
	fastForwardIfRejoining() {
		const room = this.props.room;
		if (!room.rejoining || !room.side) return;
		room.rejoining = false;
		room.battle.seekTurn(Infinity);
	}
	override receiveLine(args: Args) {
		const room = this.props.room;
		switch (args[0]) {
		case 'cantleave':
			room.requireForfeit = true;
			return;
		case 'allowleave':
			room.requireForfeit = false;
			return;
		case 'initdone':
			if (!PS.prefs.spectatefromstart) room.battle.seekTurn(Infinity);
			return;
		case 'request':
			this.receiveRequest(args[1] ? JSON.parse(args[1]) : null);
			return;
		case 'win': case 'tie':
			this.receiveRequest(null);
			break;
		case 'c': case 'c:': case 'chat': case 'chatmsg': case 'inactive':
			room.battle.instantAdd('|' + args.join('|'));
			return;
		case 'error':
			if (args[1].startsWith('[Invalid choice]') && room.request) {
				room.choices = new BattleChoiceBuilder(room.request);
				room.updateChoiceNotification();
				room.update(null);
			}
			break;
		case 'sentchoice':
			if (room.request) {
				let choices = new BattleChoiceBuilder(room.request);
				const possibleError = choices.addChoices(args[1]);
				if (possibleError || !choices.isDone()) {
					choices = new BattleChoiceBuilder(room.request);
					choices.serializedChoice = args[1];
				}
				room.choices = choices;
			}
			room.updateChoiceNotification();
			room.update(null);
			return;
		}
		room.battle.add('|' + args.join('|'));
		if (PS.prefs.noanim) this.props.room.battle.seekTurn(Infinity);
	}
	receiveRequest(request: BattleRequest | null) {
		const room = this.props.room;
		if (!request) {
			room.request = null;
			room.choices = null;
			room.updateChoiceNotification();
			return;
		}

		if (PS.prefs.autotimer && !room.battle.kickingInactive && !room.autoTimerActivated) {
			this.send('/timer on');
			room.autoTimerActivated = true;
		}

		BattleChoiceBuilder.fixRequest(request, room.battle);

		if (request.side) {
			const wasPlayer = !!room.side;
			room.battle.myPokemon = request.side.pokemon;
			room.battle.setViewpoint(request.side.id);
			room.side = request.side;
			if (!wasPlayer) this.fastForwardIfRejoining();
			// Deja de ser espectador: soltar onepanel + compose listeners
			this.etWasSpectator = false;
			this.syncSpectatorModeBindings();
		}
		if (request.ally) {
			room.battle.myAllyPokemon = request.ally.pokemon;
		}

		room.request = request;
		room.choices = new BattleChoiceBuilder(request);
		// A reconnect can send `|sentchoice|` immediately after `|request|`.
		// Wait until the entire protocol message has been processed before notifying.
		Promise.resolve().then(() => room.updateChoiceNotification());
		room.update(null);
	}
	renderConnectError() {
		const room = this.props.room;
		if (room.connectMode !== 'deleted' && room.connectMode !== 'not-found') {
			return null;
		}
		return <div class="pad"><div class="broadcast-red pad">
			<h3>{room.connectError || trUI("Error")}</h3>
			<p class="buttonbar"><button class="button" data-cmd="/close"><strong>{trUI("Close")}</strong></button></p>
		</div></div>;
	}
	/** Scoreboard: PNG modelo (near/far) + 6 picons centrados en los hex del asset. */
	buildSpectatorWingHtml(side: { name: string, n: number, isFar?: boolean }, posStr: string): string {
		const battle = this.props.room.battle;
		if (!battle?.scene) return '';
		const raw = battle.scene.getSidebarHTML(side as any, posStr);
		const tmp = document.createElement('div');
		tmp.innerHTML = raw;
		const trainer = tmp.querySelector('.trainer');
		if (!trainer) return raw;

		const nameEl = trainer.querySelector('strong');
		const name = BattleLog.escapeHTML(nameEl?.textContent || side.name || '');
		const sprite = trainer.querySelector('.trainersprite');
		const spriteHtml = sprite ? sprite.outerHTML : '';

		const iconNodes = trainer.querySelectorAll('.teamicons .picon');
		let hexes = '';
		let count = 0;
		iconNodes.forEach(node => {
			if (count >= 6) return;
			hexes += `<span class="et-sb-hex">${node.outerHTML}</span>`;
			count++;
		});
		while (count < 6) {
			hexes += `<span class="et-sb-hex"><span class="picon" style="opacity:0.35"></span></span>`;
			count++;
		}

		return (
			`<div class="trainer trainer-${posStr}">` +
			`<strong>${name}</strong>` +
			spriteHtml +
			`<div class="teamicons et-sb-hexrow">${hexes}</div>` +
			`</div>`
		);
	}
	/** Scoreboard estilo skin: hexes | VERSUS/turno | hexes (colores Eternity). */
	renderSpectatorVsBar(battleWidth: number) {
		const battle = this.props.room.battle;
		if (!battle?.scene) return null;
		const near = battle.nearSide;
		const far = battle.farSide;
		if (!near || !far) return null;

		const leftHtml = this.buildSpectatorWingHtml(near, 'near');
		const rightHtml = this.buildSpectatorWingHtml(far, 'far');

		const turnNum = battle.turn > 0 ? battle.turn : 0;
		const turnLabel = turnNum > 0
			? `${trUI("Turn")} ${turnNum}`
			: trUI("Turn") + ' —';

		return <div
			class="et-spectator-vs-bar"
			style={`width:${battleWidth}px`}
			role="group"
			aria-label="Versus"
		>
			<div class="et-spectator-scoreboard">
				<div
					class="et-sb-wing et-sb-near"
					dangerouslySetInnerHTML={{ __html: leftHtml }}
				/>
				<div class="et-sb-versus">
					<span class="et-sb-versus-text">VERSUS</span>
					<span class="et-sb-turn">{turnLabel}</span>
				</div>
				<div
					class="et-sb-wing et-sb-far"
					dangerouslySetInnerHTML={{ __html: rightHtml }}
				/>
			</div>
		</div>;
	}
	renderControls(overlayVersion = false, hidePlayerControls = false) {
		const room = this.props.room;
		if (!room.battle) return null;
		if (overlayVersion) {
			if (!room.side || !room.request || room.battle.ended) return null;
			return this.renderPlayerControls(room.request, true);
		}
		if (room.battle.ended) return this.renderAfterBattleControls();
		if (room.side && room.request) {
			if (hidePlayerControls) return null;
			return this.renderPlayerControls(room.request);
		}
		if (room.battle.stepQueue.length === 0) return null;

		const atStart = !room.battle.started;
		const atEnd = room.battle.atQueueEnd;
		const btn = (opts: {
			cmd: string, icon: string, label: string, disabled?: boolean,
			first?: boolean, last?: boolean, wide?: boolean,
		}) => (
			<button
				class={`button et-spec-btn${opts.first ? ' button-first' : ''}${opts.last ? ' button-last' : ''}${opts.disabled ? ' disabled' : ''}`}
				data-cmd={opts.cmd}
				aria-disabled={opts.disabled || undefined}
			>
				<i class={`fa ${opts.icon}`} aria-hidden></i>
				<span class="et-spec-btn-label">{opts.label}</span>
			</button>
		);

		// Espectador: tabla de bordes invisibles — reproducción | acciones+tools
		if (!room.side) {
			return <div class="inline-controls et-spectator-controls-wrap">
				<table class="et-spectator-controls-table" cellPadding={0} cellSpacing={0}>
					<tbody>
						<tr class="et-spec-row et-spec-row-playback">
							<td class="et-spec-cell et-spec-cell-play">
								{atEnd ? btn({ cmd: '/play', icon: 'fa-play', label: trUI("Play"), disabled: true }) :
									room.battle.paused ?
										btn({ cmd: '/play', icon: 'fa-play', label: trUI("Play") }) :
										btn({ cmd: '/pause', icon: 'fa-pause', label: trUI("Pause") })}
							</td>
							{!room.battle.hardcoreMode ? <>
								<td class="et-spec-cell">
									{btn({ cmd: '/ffto 0', icon: 'fa-undo', label: trUI("First turn"), disabled: atStart, first: true })}
								</td>
								<td class="et-spec-cell">
									{btn({ cmd: '/ffto -1', icon: 'fa-step-backward', label: trUI("Prev turn"), disabled: atStart })}
								</td>
								<td class="et-spec-cell">
									{btn({ cmd: '/ffto +1', icon: 'fa-step-forward', label: trUI("Skip turn"), disabled: atEnd })}
								</td>
								<td class="et-spec-cell">
									{btn({ cmd: '/ffto end', icon: 'fa-fast-forward', label: trUI("Skip to end"), disabled: atEnd, last: true })}
								</td>
							</> : <td class="et-spec-cell" colSpan={4}></td>}
						</tr>
						<tr class="et-spec-row et-spec-row-actions">
							<td class="et-spec-cell" colSpan={room.battle.hardcoreMode ? 3 : 2}>
								{btn({ cmd: '/switchsides', icon: 'fa-random', label: trUI("Switch viewpoint"), wide: true })}
							</td>
							{!room.battle.hardcoreMode && <td class="et-spec-cell">
								{btn({ cmd: '/ffto', icon: 'fa-share', label: trUI("Go to turn"), wide: true })}
							</td>}
							<td class="et-spec-cell et-spectator-tools et-spectator-tool-roomid"></td>
							<td class="et-spec-cell et-spectator-tools et-spectator-tool-replay"></td>
						</tr>
					</tbody>
				</table>
			</div>;
		}

		return <div class="inline-controls">
			<p>
				{atEnd ? (
					<button class="button disabled" aria-disabled data-cmd="/play" style="min-width:4.5em">
						<i class="fa fa-play" aria-hidden></i><br />{trUI("Play")}
					</button>
				) : room.battle.paused ? (
					<button class="button" data-cmd="/play" style="min-width:4.5em">
						<i class="fa fa-play" aria-hidden></i><br />{trUI("Play")}
					</button>
				) : (
					<button class="button" data-cmd="/pause" style="min-width:4.5em">
						<i class="fa fa-pause" aria-hidden></i><br />{trUI("Pause")}
					</button>
				)} {}
				{!room.battle.hardcoreMode && <>
					<button class={"button button-first" + (atStart ? " disabled" : "")} data-cmd="/ffto 0" style="margin-right:2px">
						<i class="fa fa-undo" aria-hidden></i><br />{trUI("First turn")}
					</button>
					<button class={"button button-first" + (atStart ? " disabled" : "")} data-cmd="/ffto -1">
						<i class="fa fa-step-backward" aria-hidden></i><br />{trUI("Prev turn")}
					</button>
					<button class={"button button-last" + (atEnd ? " disabled" : "")} data-cmd="/ffto +1" style="margin-right:2px">
						<i class="fa fa-step-forward" aria-hidden></i><br />{trUI("Skip turn")}
					</button>
					<button class={"button button-last" + (atEnd ? " disabled" : "")} data-cmd="/ffto end">
						<i class="fa fa-fast-forward" aria-hidden></i><br />{trUI("Skip to end")}
					</button>
				</>}
			</p>
			<p>
				<button class="button" data-cmd="/switchsides">
					<i class="fa fa-random" aria-hidden></i> {trUI("Switch viewpoint")}
				</button> {}
				{!room.battle.hardcoreMode && <button class="button" data-cmd="/ffto">
					<i class="fa fa-random" aria-hidden></i> {trUI("Go to turn")}
				</button>}
			</p>
		</div>;
	}
	renderMoveButton(props: {
		name: string, cmd: string, type: Dex.TypeName, tags: string, tooltip: string,
		moveData: { pp?: number, maxpp?: number, disabled?: boolean },
		/** Stable key so doubles slot changes remount buttons (avoids stale enriched labels). */
		domKey?: string,
	} | null) {
		if (!props) {
			return <button class="movebutton" disabled>&nbsp;</button>;
		}
		const pp = props.moveData.maxpp ? `${props.moveData.pp!}/${props.moveData.maxpp}` : '\u2014';
		const typeName = Dex.types.get(props.type).name || props.type;
		return <button
			key={props.domKey || props.tooltip || props.cmd}
			data-cmd={props.cmd} data-tooltip={props.tooltip}
			class={`movebutton has-tooltip ${props.moveData.disabled ? 'disabled' : `type-${props.type}`}`}
			aria-disabled={props.moveData.disabled}
		>
			{props.name}<br />
			<small class="type">{typeName} <span class="effectiveness-icon">{props.tags}</span></small> {}
			<small class="pp">{pp}</small>&nbsp;
		</button>;
	}
	renderPokemonButton(props: {
		pokemon: Pokemon | ServerPokemon | null, cmd: string, noHPBar?: boolean, disabled?: boolean | 'fade', tooltip: string,
	}) {
		const pokemon = props.pokemon;
		if (!pokemon) {
			return <button
				data-cmd={props.cmd} class={`${props.disabled ? 'disabled ' : ''}has-tooltip`}
				aria-disabled={props.disabled}
				style={props.disabled === 'fade' ? 'opacity: 0.5' : ''} data-tooltip={props.tooltip}
			>
				{trUI("(empty slot)")}
			</button>;
		}

		let hpColorClass;
		switch (BattleScene.getHPColor(pokemon)) {
		case 'y': hpColorClass = 'hpbar hpbar-yellow'; break;
		case 'r': hpColorClass = 'hpbar hpbar-red'; break;
		default: hpColorClass = 'hpbar'; break;
		}

		return <button
			data-cmd={props.cmd} class={`${props.disabled ? 'disabled ' : ''}has-tooltip`}
			aria-disabled={props.disabled}
			style={props.disabled === 'fade' ? 'opacity: 0.5' : ''} data-tooltip={props.tooltip}
		>
			{PSIcon({ pokemon })}
			{pokemon.name}
			{
				!props.noHPBar && !pokemon.fainted &&
				<span class={hpColorClass}>
					<span style={{ width: Math.round(pokemon.hp * 92 / pokemon.maxhp) || 1 }}></span>
				</span>
			}
			{!props.noHPBar && pokemon.status && <span class={`status ${pokemon.status}`}></span>}
		</button>;
	}
	renderMoveMenu(choices: BattleChoiceBuilder, overlayVersion?: boolean) {
		const moveRequest = choices.currentMoveRequest()!;

		const canDynamax = moveRequest.canDynamax && !choices.alreadyMax;
		const canMegaEvo = moveRequest.canMegaEvo && !choices.alreadyMega;
		const canMegaEvoX = moveRequest.canMegaEvoX && !choices.alreadyMega;
		const canMegaEvoY = moveRequest.canMegaEvoY && !choices.alreadyMega;
		const canZMove = moveRequest.zMoves && !choices.alreadyZ;
		const canUltraBurst = moveRequest.canUltraBurst;
		const canTerastallize = moveRequest.canTerastallize;

		const maybeDisabled = moveRequest.maybeDisabled;
		const maybeLocked = moveRequest.maybeLocked;

		return <div class="movemenu">
			{maybeDisabled && <p><em class="movewarning">
				{getClientLanguage() === 'spanish' ?
					<>¡Puede que tengas algunos movimientos <strong>anulados</strong>, así que no podrás cancelar un ataque!</> :
					<>You <strong>might</strong> have some moves disabled, so you won't be able to cancel an attack!</>}
			</em></p>}
			{maybeLocked && <p><em class="movewarning">
				{getClientLanguage() === 'spanish' ?
					<>Puede que estés <strong>obligado</strong> a un movimiento. </> :
					<>You <strong>might</strong> be locked into a move. </>}
				{}
				<button class="button" data-cmd="/choose testfight">{trUI("Try Fight button")}</button> {}
				{trUI("(prevents switching if you're locked)")}
			</em></p>}
			{!overlayVersion && this.renderMoveControls(moveRequest, choices)}
			<div class="megaevo-box">
				{canDynamax && <label class={`megaevo${choices.current.max ? ' cur' : ''}`} onClick={this.toggleBoostedMoveLabel}>
					<input type="checkbox" name="max" checked={choices.current.max} tabIndex={-1} aria-hidden="true" readOnly /> {}
					{moveRequest.gigantamax ? trUI("Gigantamax") : trUI("Dynamax")}
				</label>}
				{canMegaEvo && <label class={`megaevo${choices.current.mega ? ' cur' : ''}`} onClick={this.toggleBoostedMoveLabel}>
					<input type="checkbox" name="mega" checked={choices.current.mega} tabIndex={-1} aria-hidden="true" readOnly /> {}
					{trUI("Mega Evolution")}
				</label>}
				{canMegaEvoX && <label class={`megaevo${choices.current.mega ? ' cur' : ''}`} onClick={this.toggleBoostedMoveLabel}>
					<input type="checkbox" name="megax" checked={choices.current.megax} tabIndex={-1} aria-hidden="true" readOnly /> {}
					{trUI("Mega Evolution X")}
				</label>}
				{canMegaEvoY && <label class={`megaevo${choices.current.mega ? ' cur' : ''}`} onClick={this.toggleBoostedMoveLabel}>
					<input type="checkbox" name="megay" checked={choices.current.megay} tabIndex={-1} aria-hidden="true" readOnly /> {}
					{trUI("Mega Evolution Y")}
				</label>}
				{canUltraBurst && <label class={`megaevo${choices.current.ultra ? ' cur' : ''}`} onClick={this.toggleBoostedMoveLabel}>
					<input type="checkbox" name="ultra" checked={choices.current.ultra} tabIndex={-1} aria-hidden="true" readOnly /> {}
					{trUI("Ultra Burst")}
				</label>}
				{canZMove && <label class={`megaevo${choices.current.z ? ' cur' : ''}`} onClick={this.toggleBoostedMoveLabel}>
					<input type="checkbox" name="z" checked={choices.current.z} tabIndex={-1} aria-hidden="true" readOnly /> {}
					{trUI("Z-Power")}
				</label>}
				{canTerastallize && <label class={`megaevo${choices.current.tera ? ' cur' : ''}`} onClick={this.toggleBoostedMoveLabel}>
					<input type="checkbox" name="tera" checked={choices.current.tera} tabIndex={-1} aria-hidden="true" readOnly /> {}
					{trUI("Tera")} {PSIcon({ type: canTerastallize, new: true, tera: true })}
				</label>}
			</div>
			{overlayVersion && this.renderMoveControls(moveRequest, choices)}
		</div>;
	}
	/**
	 * Active slot can be null briefly (faint/switch/re-render).
	 * Never fall back to another active slot — in doubles that made slot-2
	 * tooltips/effectiveness use slot-1's Pokémon.
	 */
	resolveActivePokemon(
		battle: Battle, activeIndex: number, serverPokemon: ServerPokemon | null | undefined
	): Pokemon | null {
		const near = battle.nearSide;
		const slot = near.active[activeIndex];
		if (slot) return slot;
		if (serverPokemon) {
			const found = battle.findCorrespondingPokemon(serverPokemon);
			if (found) return found;
		}
		return null;
	}
	renderMoveControls(active: BattleRequestActivePokemon, choices: BattleChoiceBuilder) {
		const battle = this.props.room.battle;
		const dex = battle.dex;
		const pokemonIndex = choices.index();
		const activeIndex = battle.mySide.n > 1 ? pokemonIndex + battle.pokemonControlled : pokemonIndex;
		const serverPokemon = choices.request.side!.pokemon[pokemonIndex];
		const clientPokemon = this.resolveActivePokemon(battle, activeIndex, serverPokemon);
		const valueTracker = new ModifiableValue(battle, clientPokemon, serverPokemon);
		const tooltips = (battle.scene as BattleScene).tooltips;

		if (choices.current.max || (active.maxMoves && !active.canDynamax)) {
			if (!active.maxMoves) {
				return <div class="message-error">{trUI("Maxed with no max moves")}</div>;
			}
			const gmax = active.gigantamax && dex.moves.get(active.gigantamax);
			return active.moves.map((moveData, i) => {
				const move = dex.moves.get(moveData.name);
				const [moveType, tags] = tooltips.getMoveTypeText(move, valueTracker, gmax || true);
				let maxMoveData: { name: string, id: ID } = active.maxMoves![i];
				if (maxMoveData.name !== 'Max Guard') {
					maxMoveData = tooltips.getMaxMoveFromType(moveType, gmax);
				}
				const gmaxTooltip = maxMoveData.id.startsWith('gmax') ? `|${maxMoveData.id}` : ``;
				const tooltip = `maxmove|${moveData.name}|${pokemonIndex}${gmaxTooltip}`;
				return this.renderMoveButton({
					name: maxMoveData.name,
					cmd: `/move ${i + 1} max`,
					type: moveType,
					tags,
					tooltip,
					moveData,
					domKey: `max-${pokemonIndex}-${i}-${maxMoveData.name}`,
				});
			});
		}

		if (choices.current.z) {
			if (!active.zMoves) {
				return <div class="message-error">{trUI("No Z moves")}</div>;
			}
			return active.moves.map((moveData, i) => {
				const zMoveData = active.zMoves![i];
				if (!zMoveData) {
					return this.renderMoveButton(null);
				}
				const specialMove = dex.moves.get(zMoveData.name);
				const move = specialMove.exists ? specialMove : dex.moves.get(moveData.name);
				const [moveType, tags] = tooltips.getMoveTypeText(move, valueTracker);
				const tooltip = `zmove|${moveData.name}|${pokemonIndex}`;
				return this.renderMoveButton({
					name: zMoveData.name,
					cmd: `/move ${i + 1} zmove`,
					type: moveType,
					tags,
					tooltip,
					moveData: { pp: 1, maxpp: 1 },
					domKey: `z-${pokemonIndex}-${i}-${zMoveData.name}`,
				});
			});
		}

		const special = choices.moveSpecial(choices.current);
		return active.moves.map((moveData, i) => {
			const move = dex.moves.get(moveData.name);
			const [moveType, tags] = tooltips.getMoveTypeText(move, valueTracker);
			const tooltip = `move|${moveData.name}|${pokemonIndex}`;
			return this.renderMoveButton({
				name: move.name,
				cmd: `/move ${i + 1}${special}`,
				type: moveType,
				tags,
				tooltip,
				moveData,
				domKey: `m-${pokemonIndex}-${i}-${moveData.name}`,
			});
		});
	}
	renderMoveTargetControls(request: BattleMoveRequest, choices: BattleChoiceBuilder) {
		const battle = this.props.room.battle;
		let moveTarget = choices.currentMove()?.target;
		if ((moveTarget === 'adjacentAlly' || moveTarget === 'adjacentFoe') && battle.gameType === 'freeforall') {
			moveTarget = 'normal';
		}
		const moveChoice = choices.stringChoice(choices.current);

		const userSlot = choices.index() + Math.floor(battle.mySide.n / 2) * battle.pokemonControlled;
		const userSlotCross = battle.farSide.active.length - 1 - userSlot;

		return <>
			{battle.farSide.active.map((pokemon, i) => {
				let disabled = false;
				if (moveTarget === 'adjacentAlly' || moveTarget === 'adjacentAllyOrSelf') {
					disabled = true;
				} else if (moveTarget === 'normal' || moveTarget === 'adjacentFoe') {
					if (Math.abs(userSlotCross - i) > 1) disabled = true;
				}

				if (pokemon?.fainted) pokemon = null;
				return this.renderPokemonButton({
					pokemon,
					cmd: disabled ? `` : `/${moveChoice} +${i + 1}`,
					disabled: disabled && 'fade',
					tooltip: `activepokemon|1|${i}`,
				});
			}).reverse()}
			<div style={{ clear: 'left' }}></div>
			{battle.nearSide.active.map((pokemon, i) => {
				let disabled = false;
				if (moveTarget === 'adjacentFoe') {
					disabled = true;
				} else if (moveTarget === 'normal' || moveTarget === 'adjacentAlly' || moveTarget === 'adjacentAllyOrSelf') {
					if (Math.abs(userSlot - i) > 1) disabled = true;
				}
				if (moveTarget !== 'adjacentAllyOrSelf' && userSlot === i) disabled = true;

				if (pokemon?.fainted) pokemon = null;
				return this.renderPokemonButton({
					pokemon,
					cmd: disabled ? `` : `/${moveChoice} -${i + 1}`,
					disabled: disabled && 'fade',
					tooltip: `activepokemon|0|${i}`,
				});
			})}
		</>;
	}
	renderSwitchMenu(
		request: BattleMoveRequest | BattleSwitchRequest, choices: BattleChoiceBuilder, ignoreTrapping?: boolean
	) {
		const numActive = choices.requestLength();
		const maybeTrapped = !ignoreTrapping && choices.currentMoveRequest()?.maybeTrapped;
		const trapped = !ignoreTrapping && !maybeTrapped && choices.currentMoveRequest()?.trapped;
		const isReviving = choices.isReviving();

		return <div class="switchmenu">
			{maybeTrapped && <em class="movewarning">
				{getClientLanguage() === 'spanish' ?
					<>¡Puede que estés <strong>atrapado</strong>, así que no podrás cancelar un cambio!</> :
					<>You <strong>might</strong> be trapped, so you won't be able to cancel a switch!</>}<br />
			</em>}
			{trapped && <em class="movewarning">
				{getClientLanguage() === 'spanish' ?
					<>¡Estás <strong>atrapado</strong> y no puedes cambiar!</> :
					<>You're <strong>trapped</strong> and cannot switch!</>}<br />
			</em>}
			{isReviving && <em class="movewarning">
				{trUI("Choose a Pokémon to revive!")}<br />
			</em>}
			{request.side.pokemon.map((serverPokemon, i) => {
				let cantSwitch = trapped || i < numActive || choices.alreadySwitchingIn.includes(i + 1) || serverPokemon.fainted;
				if (isReviving) cantSwitch = !serverPokemon.fainted || choices.alreadySwitchingIn.includes(i + 1);
				return this.renderPokemonButton({
					pokemon: serverPokemon,
					cmd: `/switch ${i + 1}`,
					disabled: cantSwitch,
					tooltip: `switchpokemon|${i}`,
				});
			})}
			{request.ally?.pokemon?.map((serverPokemon, i) => {
				return this.renderPokemonButton({
					pokemon: serverPokemon,
					cmd: `/switch notMine`,
					disabled: true,
					tooltip: `allypokemon|${i}`,
				});
			})}
		</div>;
	}
	renderTeamPreviewChooser(request: | BattleTeamRequest, choices: BattleChoiceBuilder) {
		return request.side.pokemon.map((serverPokemon, i) => {
			const cantSwitch = choices.alreadySwitchingIn.includes(i + 1);
			return this.renderPokemonButton({
				pokemon: serverPokemon,
				cmd: `/switch ${i + 1}`,
				disabled: cantSwitch && 'fade',
				tooltip: `switchpokemon|${i}`,
			});
		});
	}
	renderTeamList(overlayVersion = false) {
		const team = this.team;
		if (!team) return;
		return <div class="switchcontrols">
			{!overlayVersion && <h3 class="switchselect">{trUI("Team")}</h3>}
			<div class="switchmenu">
				{team.map((serverPokemon, i) => {
					return this.renderPokemonButton({
						pokemon: serverPokemon,
						cmd: "",
						disabled: true,
						tooltip: `switchpokemon|${i}`,
					});
				})}
			</div>
		</div>;
	}
	renderChosenTeam(request: BattleTeamRequest, choices: BattleChoiceBuilder) {
		return choices.alreadySwitchingIn.map(slot => {
			const serverPokemon = request.side.pokemon[slot - 1];
			return this.renderPokemonButton({
				pokemon: serverPokemon,
				cmd: `/switch ${slot}`,
				tooltip: `switchpokemon|${slot - 1}`,
			});
		});
	}
	renderOldChoices(request: BattleRequest, choices: BattleChoiceBuilder, overlayVersion = false) {
		if (!choices) return null; // should not happen
		if (
			(request.requestType !== 'move' && request.requestType !== 'switch' && request.requestType !== 'team') ||
			choices.isEmpty()
		) {
			return null;
		}

		let buf: preact.ComponentChild[] = [
			<button data-cmd="/cancelone" class="button"><i class="fa fa-chevron-left" aria-hidden></i> {trUI("Back")}</button>, ' ',
		];
		if (choices.isDone() && (
			choices.noCancel || this.props.room.battle.hardcoreMode ||
			(choices.choices.length <= 1 && !overlayVersion)
		)) {
			buf = [];
		}

		if (choices.serializedChoice) {
			if (choices.serializedChoice === 'default') {
				return [trUI(`Automatic choice`), <br />];
			}
			return [trUI(`Unrecognized choice from server: `), <code>{choices.serializedChoice}</code>, <br />];
		}

		const battle = this.props.room.battle;
		const es = getClientLanguage() === 'spanish';
		let teamList = false;
		for (let i = 0; i < choices.choices.length; i++) {
			const choiceString = choices.choices[i];
			if (choiceString === "testfight") {
				buf.push(es ?
					`${request.side.pokemon[i].name} está obligado a un movimiento.` :
					`${request.side.pokemon[i].name} is locked into a move.`);
				return buf;
			}
			let choice;
			try {
				choice = choices.parseChoice(choiceString, i);
			} catch (err: any) {
				buf.push(<span class="message-error">{err.message}</span>);
			}
			if (!choice) continue;
			const pokemon = request.side.pokemon[i];
			const active = request.requestType === 'move' ? request.active[i] : null;
			if (choice.choiceType === 'move') {
				if (es) {
					buf.push(`${pokemon.name} `);
					if (choice.mega) buf.push(<strong>{trUI("Mega")}</strong>, trUI(" Evolve and "));
					if (choice.megax) buf.push(<strong>{trUI("Mega")}</strong>, trUI(" Evolve (X) and "));
					if (choice.megay) buf.push(<strong>{trUI("Mega")}</strong>, trUI(" Evolve (Y) and "));
					if (choice.ultra) buf.push(<strong>{trUI("Ultra")}</strong>, trUI(" Burst and "));
					if (choice.tera) buf.push(`teracristalizará (`, <strong>{active?.canTerastallize || '???'}</strong>, `) y `);
					if (choice.max && active?.canDynamax) buf.push(active?.gigantamax ? `usará Gigamax y ` : `usará Dynamax y `);
					buf.push(`usará `, <strong>{choices.currentMove(choice, i)?.name}</strong>);
					if (choice.targetLoc > 0) {
						const target = battle.farSide.active[choice.targetLoc - 1];
						if (!target) {
							buf.push(` en el puesto ${choice.targetLoc}`);
						} else {
							buf.push(` contra ${target.name}`);
						}
					} else if (choice.targetLoc < 0) {
						const target = battle.nearSide.active[-choice.targetLoc - 1];
						if (!target) {
							buf.push(` en el puesto aliado ${-choice.targetLoc}`);
						} else {
							buf.push(` contra el aliado ${target.name}`);
						}
					}
					buf.push(`.`);
				} else {
					buf.push(`${pokemon.name} will `);
					if (choice.mega) buf.push(<strong>{trUI("Mega")}</strong>, trUI(" Evolve and "));
					if (choice.megax) buf.push(<strong>{trUI("Mega")}</strong>, trUI(" Evolve (X) and "));
					if (choice.megay) buf.push(<strong>{trUI("Mega")}</strong>, trUI(" Evolve (Y) and "));
					if (choice.ultra) buf.push(<strong>{trUI("Ultra")}</strong>, trUI(" Burst and "));
					if (choice.tera) buf.push(`Terastallize (`, <strong>{active?.canTerastallize || '???'}</strong>, `) and `);
					if (choice.max && active?.canDynamax) buf.push(active?.gigantamax ? `Gigantamax and ` : `Dynamax and `);
					buf.push(`use `, <strong>{choices.currentMove(choice, i)?.name}</strong>);
					if (choice.targetLoc > 0) {
						const target = battle.farSide.active[choice.targetLoc - 1];
						if (!target) {
							buf.push(` at slot ${choice.targetLoc}`);
						} else {
							buf.push(` at ${target.name}`);
						}
					} else if (choice.targetLoc < 0) {
						const target = battle.nearSide.active[-choice.targetLoc - 1];
						const ally = battle.gameType !== 'freeforall' ? 'ally' : '';
						if (!target) {
							buf.push(` at ${ally} slot ${choice.targetLoc}`);
						} else {
							buf.push(` at ${ally} ${target.name}`);
						}
					}
					buf.push(`.`);
				}
			} else if (choice.choiceType === 'switch') {
				const target = request.side.pokemon[choice.targetPokemon - 1];
				if (choices.isReviving(i)) {
					buf.push(es ? `${pokemon.name} revivirá a ` : `${pokemon.name} will revive `, <strong>{target.name}</strong>, `.`);
				} else {
					buf.push(es ? `${pokemon.name} cambiará a ` : `${pokemon.name} will switch to `, <strong>{target.name}</strong>, `.`);
				}
			} else if (choice.choiceType === 'shift') {
				buf.push(es ?
					<>{pokemon.name} se <strong>desplazará</strong> al centro.</> :
					<>{pokemon.name} will <strong>shift</strong> to the center.</>);
			} else if (choice.choiceType === 'team') {
				const target = request.side.pokemon[choice.targetPokemon - 1];
				buf.push(teamList ? `, ` : (es ? `Elegiste ` : `You picked `), <strong>{target.name}</strong>);
				teamList = true;
			}
			if (!teamList) buf.push(<br />);
		}
		if (teamList) {
			buf.push('.', <br />);
		}
		return buf;
	}
	overlayControlClass(overlay: 'move' | 'switch') {
		return `button ${overlay}-button${this.props.room.overlayActive === overlay ? ' cur' : ''}`;
	}
	renderPlayerAnimationControls(overlayVersion = false) {
		const room = this.props.room;
		if (overlayVersion) {
			const canSkip = !room.battle.hardcoreMode;
			return <>
				{canSkip && <div class="overlay-controls-skip">
					<button class="button" data-cmd="/ffto end"><i class="fa fa-fast-forward" aria-hidden></i><br />{trUI("Skip")}</button>
				</div>}
			</>;
		}
		return <div class="inline-controls">
			{!room.battle.hardcoreMode && <div class="whatdo" style="padding-bottom:0">
				<button class="button" data-cmd="/ffto end"><i class="fa fa-fast-forward" aria-hidden></i><br />{trUI("Skip animation")}</button>
			</div>}
			{this.renderTeamList()}
		</div>;
	}
	renderPlayerMoveControls(request: BattleMoveRequest, choices: BattleChoiceBuilder, overlayVersion = false) {
		const room = this.props.room;
		const index = choices.index();
		const pokemon = request.side.pokemon[index];

		if (choices.current.move) {
			const moveName = choices.currentMove()?.name;
			if (overlayVersion) {
				return <>
					<div class="overlay-controls-list">
						<button class="button move-button cur"><strong>{trUI("Battle")}</strong></button> {}
						<button class="button switch-button disabled"><strong>{trUI("Switch")}</strong></button>
					</div>
					<div class="targetcontrols">
						<p class="overlay-message">
							{this.renderOldChoices(request, choices, true)}
							{getClientLanguage() === 'spanish' ?
								<>¿Dónde debería usar <strong>{moveName}</strong> {pokemon.name}?</> :
								<>{pokemon.name} should use <strong>{moveName}</strong> at where?</>}
						</p>
						<div class="switchmenu">
							{this.renderMoveTargetControls(request, choices)}
						</div>
					</div>
				</>;
			}
			return <div class="inline-controls">
				<div class="whatdo">
					{this.renderOldChoices(request, choices)}
					{getClientLanguage() === 'spanish' ?
						<>¿Dónde debería usar <strong>{moveName}</strong> {pokemon.name}? </> :
						<>{pokemon.name} should use <strong>{moveName}</strong> at where? </>} {}
				</div>
				<div class="switchcontrols">
					<div class="switchmenu">
						{this.renderMoveTargetControls(request, choices)}
					</div>
				</div>
			</div>;
		}

		const canShift = room.battle.gameType === 'triples' && index !== 1;

		if (overlayVersion) {
			return <>
				<div class="overlay-controls-list">
					<button class={this.overlayControlClass('move')} data-cmd="/movemenu"><strong>{trUI("Battle")}</strong></button> {}
					<button class={this.overlayControlClass('switch')} data-cmd="/switchmenu"><strong>{trUI("Switch")}</strong></button>
				</div>
				{!room.overlayActive && <div class="whatdo">
					{this.renderOldChoices(request, choices, true)}
					{getClientLanguage() === 'spanish' ?
						<>¿Qué hará <strong>{pokemon.name}</strong>?</> :
						<>What will <strong>{pokemon.name}</strong> do?</>}
				</div>}
				{room.overlayActive === 'move' && <div class="movecontrols">
					{this.renderMoveMenu(choices, true)}
				</div>}
				{room.overlayActive === 'switch' && <div class="switchcontrols">
					{canShift && (
						<button data-cmd="/shift">{trUI("Move to center")}</button>
					)}
					{this.renderSwitchMenu(request, choices)}
				</div>}
			</>;
		}
		return <div class="inline-controls">
			<div class="whatdo">
				{this.renderOldChoices(request, choices)}
				{getClientLanguage() === 'spanish' ?
					<>¿Qué hará <strong>{pokemon.name}</strong>?</> :
					<>What will <strong>{pokemon.name}</strong> do?</>}
			</div>
			<div class="movecontrols">
				<h3 class="moveselect">{trUI("Battle")}</h3>
				{this.renderMoveMenu(choices)}
			</div>
			<div class="switchcontrols">
				{canShift && [
					<h3 class="shiftselect">{trUI("Shift")}</h3>,
					<button data-cmd="/shift">{trUI("Move to center")}</button>,
				]}
				<h3 class="switchselect">{trUI("Switch")}</h3>
				{this.renderSwitchMenu(request, choices)}
			</div>
		</div>;
	}
	renderPlayerSwitchControls(request: BattleSwitchRequest, choices: BattleChoiceBuilder, overlayVersion = false) {
		const pokemon = request.side.pokemon[choices.index()];
		const prompt = choices.isReviving() ?
			(getClientLanguage() === 'spanish' ?
				<>¿A quién revivirá <strong>{pokemon.name}</strong>?</> :
				<>Who will <strong>{pokemon.name}</strong> revive?</>) :
			(getClientLanguage() === 'spanish' ?
				<>¿Quién reemplazará a <strong>{pokemon.name}</strong>?</> :
				<>Who will replace <strong>{pokemon.name}</strong>?</>);
		if (overlayVersion) {
			return <>
				<div class="overlay-controls-list">
					<button class="button switch-button cur"><strong>{trUI("Switch")}</strong></button>
				</div>
				<div class="switchcontrols">
					<p class="overlay-message">
						{this.renderOldChoices(request, choices, true)}
						{prompt}
					</p>
					{this.renderSwitchMenu(request, choices, true)}
				</div>
			</>;
		}
		return <div class="inline-controls">
			<div class="whatdo">
				{this.renderOldChoices(request, choices)}
				{prompt}
			</div>
			<div class="switchcontrols">
				<h3 class="switchselect">{trUI("Switch")}</h3>
				{this.renderSwitchMenu(request, choices, true)}
			</div>
		</div>;
	}
	renderPlayerTeamPreviewControls(request: BattleTeamRequest, choices: BattleChoiceBuilder, overlayVersion = false) {
		const prompt = choices.alreadySwitchingIn.length > 0 ? (
			[<button data-cmd="/cancelone" class="button"><i class="fa fa-chevron-left" aria-hidden></i> {trUI("Back")}</button>,
				` ${trUI("What about the rest of your team?")} `]
		) : (
			`${trUI("How will you start the battle?")} `
		);
		const chosenTeamSizeLabel = (request.chosenTeamSize || 0) > 1 ? ` / ${request.chosenTeamSize!}` : '';
		const chooseLabel = choices.alreadySwitchingIn.length <= 0 ?
			`${trUI("lead")}${chosenTeamSizeLabel}` : `${trUI("slot ")}${choices.alreadySwitchingIn.length + 1}${chosenTeamSizeLabel}`;
		if (overlayVersion) {
			return <>
				<div class="overlay-controls-list">
					<button class="button switch-button cur"><strong>{trUI("Team")}</strong></button>
				</div>
				<div class="teamcontrols">
					<p class="overlay-message">{prompt}</p>
					<h3 class="switchselect">{trUI("Choose")} {chooseLabel}</h3>
					<div class="switchmenu">
						{this.renderTeamPreviewChooser(request, choices)}
						<div style="clear:left"></div>
					</div>
					{choices.alreadySwitchingIn.length > 0 && <>
						<h3 class="switchselect">{trUI("Team so far")}</h3>
						<div class="switchmenu">
							{this.renderChosenTeam(request, choices)}
						</div>
					</>}
				</div>
			</>;
		}
		return <div class="inline-controls">
			<div class="whatdo">
				{prompt}
			</div>
			<div class="switchcontrols">
				<h3 class="switchselect">
					{trUI("Choose")} {chooseLabel}
				</h3>
				<div class="switchmenu">
					{this.renderTeamPreviewChooser(request, choices)}
					<div style="clear:left"></div>
				</div>
			</div>
			<div class="switchcontrols">
				{choices.alreadySwitchingIn.length > 0 && <h3 class="switchselect">{trUI("Team so far")}</h3>}
				<div class="switchmenu">
					{this.renderChosenTeam(request, choices)}
				</div>
			</div>
		</div>;
	}
	renderPlayerControls(request: BattleRequest, overlayVersion = false) {
		const room = this.props.room;
		const atEnd = room.battle.atQueueEnd;
		if (!atEnd) return this.renderPlayerAnimationControls(overlayVersion);

		let choices = room.choices;
		if (!choices) return 'Error: Missing BattleChoiceBuilder';
		if (choices.request !== request) {
			choices = new BattleChoiceBuilder(request);
			room.choices = choices;
			room.overlayActive = null;
		}

		if (choices.isDone()) {
			if (overlayVersion) {
				return <>
					<div class="overlay-controls-list">
						<button class={this.overlayControlClass('switch')} data-cmd="/switchmenu"><strong>{trUI("Team")}</strong></button>
					</div>
					{!room.overlayActive && <div class="whatdo">
						{this.renderOldChoices(request, choices, true)}
					</div>}
					{room.overlayActive === 'switch' && this.renderTeamList(true)}
				</>;
			}
			return <div class="inline-controls">
				<div class="whatdo">
					{this.renderOldChoices(request, choices)}
					<em>{trUI("Waiting for opponent...")}</em> {choices.noCancel || room.battle.hardcoreMode ?
						null : <button data-cmd="/cancel" class="button">{trUI("Cancel")}</button>}
				</div>
				{this.renderTeamList()}
			</div>;
		}
		if (request.side) {
			room.battle.myPokemon = request.side.pokemon;
			this.team = request.side.pokemon;
		}
		switch (request.requestType) {
		case 'move':
			return this.renderPlayerMoveControls(request, choices, overlayVersion);
		case 'switch':
			return this.renderPlayerSwitchControls(request, choices, overlayVersion);
		case 'team':
			return this.renderPlayerTeamPreviewControls(request, choices, overlayVersion);
		}
		return null;
	}

	renderAfterBattleControls() {
		const room = this.props.room;
		const isNotTiny = room.width > 700;
		return <div class="inline-controls">
			<p>
				<span style="float: right">
					<a
						onClick={this.handleDownloadReplay}
						href={`//${Config.routes.replays}/download`}
						class="button replayDownloadButton"
					>
						<i class="fa fa-download" aria-hidden></i> {trUI("Download replay")}</a>
					<br />
					<br />
					<button class="button" data-cmd="/savereplay">
						<i class="fa fa-upload" aria-hidden></i> {trUI("Upload and share replay")}
					</button>
					<br />
					<button class="button eternity-copy-replay" onClick={this.handleCopyReplayLink} style={{ marginTop: 6 }}>
						<i class="fa fa-link" aria-hidden></i> {trUI("Copiar link replay")}
					</button>
				</span>

				<button class="button" data-cmd="/play" style="min-width:4.5em">
					<i class="fa fa-undo" aria-hidden></i><br />{trUI("Replay")}
				</button> {}
				{isNotTiny && !room.battle.hardcoreMode && <>
					<button class="button button-first" data-cmd="/ffto 0" style="margin-right:2px">
						<i class="fa fa-undo" aria-hidden></i><br />{trUI("First turn")}
					</button>
					<button class="button button-first" data-cmd="/ffto -1">
						<i class="fa fa-step-backward" aria-hidden></i><br />{trUI("Prev turn")}
					</button>
				</>}
			</p>
			{room.side ? (
				<p>
					<button class="button" data-cmd="/close">
						<strong>{trUI("Main menu")}</strong><br /><small>{trUI("(closes this battle)")}</small>
					</button> {}
					<button class="button" data-cmd={`/closeand /challenge ${room.battle.farSide.id},${room.battle.tier}`}>
						<strong>{trUI("Rematch")}</strong><br /><small>{trUI("(closes this battle)")}</small>
					</button>
				</p>
			) : (
				<p>
					<button class="button" data-cmd="/switchsides"><i class="fa fa-random" aria-hidden></i> {trUI("Switch viewpoint")}</button> {}
					{!room.battle.hardcoreMode && <button class="button" data-cmd="/ffto">
						<i class="fa fa-random" aria-hidden></i> {trUI("Go to turn")}
					</button>}
				</p>
			)}
		</div>;
	}

	handleDownloadReplay = (e: MouseEvent) => {
		let room = this.props.room;
		const target = e.currentTarget as HTMLAnchorElement;
		// download replay
		let filename = (room.battle.tier || 'Battle').replace(/[^A-Za-z0-9]/g, '');
		let date = new Date();
		filename += `-${date.getFullYear()}`;
		filename += `-${date.getMonth() >= 9 ? '' : '0'}${date.getMonth() + 1}`;
		filename += `-${date.getDate() >= 10 ? '' : '0'}${date.getDate()}`;
		filename += '-' + toID(room.battle.p1.name);
		filename += '-' + toID(room.battle.p2.name);
		target.href = window.BattleLog.createReplayFileHref(room);
		target.download = filename + '.html';
		e.stopPropagation();
	};
	handleCopyReplayLink = (e: Event) => {
		e.preventDefault();
		e.stopPropagation();
		const room = this.props.room;
		const id = room.id.startsWith('battle-') ? room.id.slice(7) : room.id;
		const host = Config.routes?.replays || 'replay.pokemonshowdown.com';
		const url = `https://${host}/${id}`;
		const qol = (window as any).EternityQoL;
		if (navigator.clipboard?.writeText) {
			void navigator.clipboard.writeText(url).then(() => {
				if (qol?.get) {/* toast via QoL if present */}
			});
		}
		try {
			const toast = document.getElementById('eternity-qol-toast');
			if (toast) {
				toast.textContent = 'Link de replay copiado';
				toast.classList.add('show');
				setTimeout(() => toast.classList.remove('show'), 1400);
			} else {
				PS.alert(`Replay link:\n${url}`);
			}
		} catch {
			PS.alert(`Replay link:\n${url}`);
		}
	};

	override render() {
		this.updateLayout();
		const room = this.props.room;
		const id = `room-${room.id}`;
		const hardcoreStyle = room.battle?.hardcoreMode ? <style
			dangerouslySetInnerHTML={{ __html: `#${id} .battle .turn, #${id} .battle-history { display: none !important; }` }}
		></style> : null;
		const { layout, battleHeight, battleWidth, overlayControls } = this.chooseLayout();
		const overlayVersion = overlayControls && !!room.battle && !!room.side && !!room.request && !room.battle.ended;
		const spectatorClass = !room.side ? 'et-spectator' : undefined;

		if (layout === 'scrolling') {
			// low-width-low-height layout
			// TODO: nicer phone horizontal layout
			const scrollUserList = room.width >= 320 ? 146 : 0;
			return <PSPanelWrapper room={room} focusClick noScroll="hidden" className={spectatorClass}>
				{hardcoreStyle}
				<ChatLog
					class={`battle-log${scrollUserList ? ' hasuserlist' : ''}`}
					room={room} left={scrollUserList} noSubscription hasPreempt bottom={0}
				>
					<ChatTextEntry
						room={room} onMessage={this.send} onKey={this.onKey}
						left={scrollUserList} tinyLayout={room.width - scrollUserList < 400}
					/>
					<div
						class="et-battle-stage"
						style={`height:${battleHeight}px;width:${battleWidth}px;margin: 0 auto;position:relative`}
					>
						<BattleDiv room={room} />
					</div>
					{overlayVersion && <div class="overlay-controls" style="position:relative;height:0">
						{this.renderControls(true)}
					</div>}
					<div
						class={`battle-controls inline-battle${room.width > 660 ? ' wide-controls' : ''}`}
						role="complementary" aria-label={trUI("Battle Controls")}
					>
						{this.renderControls(false, overlayVersion)}
						{this.renderConnectError()}
					</div>
				</ChatLog>
				<ChatUserList room={room} minimized={!scrollUserList} />
				{(room.battle && !room.battle.ended && room.request && room.battle.mySide.id === PS.user.userid) &&
					<TimerButton room={room} top={7} />}
				<div class="battle-controls-container"></div>
			</PSPanelWrapper>;
		}

		if (layout === 'top-and-bottom') {
			// Player phone / short vertical layout (classic PS) — not spectator stack
			return <PSPanelWrapper room={room} focusClick noScroll="hidden" className={spectatorClass}>
				{hardcoreStyle}
				<div
					class="et-battle-stage"
					style={`position:relative;height:${battleHeight}px;width:${battleWidth}px;margin:0 auto`}
				>
					<BattleDiv room={room} />
				</div>
				{overlayVersion && <div
					class="overlay-controls"
					style={`position:absolute;left:0;top:${battleHeight}px;width:100%;height:0`}
				>
					{this.renderControls(true)}
				</div>}
				<ChatLog
					class="battle-log hasuserlist" room={room} top={battleHeight} noSubscription hasPreempt
				>
					<div
						class={`battle-controls${room.width > 660 ? ' wide-controls' : ''}`}
						role="complementary" aria-label={trUI("Battle Controls")}
					>
						{this.renderControls(false, overlayVersion)}
						{this.renderConnectError()}
					</div>
				</ChatLog>
				<ChatTextEntry room={room} onMessage={this.send} onKey={this.onKey} left={0} tinyLayout={room.width < 400} />
				<ChatUserList room={room} top={battleHeight} minimized />
				{(room.battle && !room.battle.ended && room.request && room.battle.mySide.id === PS.user.userid) &&
					<TimerButton room={room} top={battleHeight + 7} />}
				<div class="battle-controls-container"></div>
			</PSPanelWrapper>;
		}

		if (room.width < 500) {
			// oldclient phone layout
			const showingChat = this.mobileChatShown;
			const mobileUserList = room.width >= 320 ? 146 : 0;
			return <PSPanelWrapper room={room} focusClick noScroll="hidden" className={spectatorClass}>
				{hardcoreStyle}
				<div class="scrollable-battle-container et-battle-stage" style={`width:${battleWidth}px;${showingChat ? 'display:none;' : ''}`}>
					<BattleDiv room={room} />
					{overlayVersion && <div
						class="overlay-controls"
						style={`position:absolute;left:0;top:${battleHeight}px;width:${battleWidth}px;height:0`}
					>
						{this.renderControls(true)}
					</div>}
					<div class="battle-controls-container">
						<div
							class={`battle-controls${battleWidth >= 639 ? ' wide-controls' : ''}`}
							role="complementary" aria-label={trUI("Battle Controls")}
							style={`top:${battleHeight + 10}px;width:${battleWidth}px;`}
						>
							{(room.battle && !room.battle.ended && room.request &&
								room.battle.mySide.id === PS.user.userid) && <TimerButton room={room} top={0} />}
							{this.renderControls(false, overlayVersion)}
							{this.renderConnectError()}
						</div>
					</div>
				</div>
				<div style={!showingChat ? 'display:none;' : ''}>
					<ChatLog
						class={`battle-log${mobileUserList ? ' hasuserlist' : ''}`}
						room={room} left={mobileUserList} noSubscription hasPreempt
					/>
					<ChatTextEntry
						room={room} onMessage={this.send} onKey={this.onKey}
						left={mobileUserList} tinyLayout
					/>
					<ChatUserList room={room} minimized={!mobileUserList} />
				</div>
				{showingChat ? (
					<button class="battle-chat-toggle button" name="hideChat" onClick={this.showMobileBattle}>
						{trUI("Battle")} <i class="fa fa-caret-right" aria-hidden></i>
					</button>
				) : (
					<button class="battle-chat-toggle button" name="showChat" onClick={this.showMobileChat}>
						<i class="fa fa-caret-left" aria-hidden></i> {trUI("Chat")}
					</button>
				)}
			</PSPanelWrapper>;
		}

		// ── Spectator cinema (separate from player) ──
		// Left rail: userlist | arena + scoreboard + controls + compose
		// Right: full battle log / chat history panel (no users popup)
		if (!room.side) {
			const userListWidth = 146;
			const chatWidth = this.spectatorChatWidth();
			const leftColumnWidth = Math.max(battleWidth + userListWidth, room.width - chatWidth);
			const battleColumnWidth = leftColumnWidth - userListWidth;
			const chatLeft = room.width - chatWidth;
			return <PSPanelWrapper room={room} focusClick noScroll="hidden" className="et-spectator">
				{hardcoreStyle}
				<style dangerouslySetInnerHTML={{
					__html: `#${id}.et-spectator{--et-spec-chat-w:${chatWidth}px;--et-spec-ulist-w:${userListWidth}px}`,
				}} />
				{/* Left rail: spectators / users only (battle options → right log) */}
				<ChatUserList room={room} left={0} hideBattleOptions />
				<div
					class="scrollable-battle-container et-battle-stage et-spectator-field"
					style={`width:${battleColumnWidth}px`}
				>
					<div class="et-spectator-stage-slot">
						<div
							class="et-spectator-stage-inner"
							style={`position:relative;width:${battleWidth}px;height:${battleHeight}px`}
						>
							<BattleDiv room={room} />
						</div>
					</div>
					{this.renderSpectatorVsBar(battleWidth)}
					<div class="battle-controls-container et-spectator-controls-host">
						<div
							class={`battle-controls${battleWidth >= 639 ? ' wide-controls' : ''} et-spectator-controls`}
							role="complementary" aria-label={trUI("Battle Controls")}
							style={`width:${battleColumnWidth}px;`}
						>
							{this.renderControls(false, false)}
							{this.renderConnectError()}
						</div>
					</div>
				</div>
				{/* Right panel: registros + chat history; options top-right */}
				<button
					data-href="battleoptions"
					class="button small et-spec-log-battleopts"
					title={trUI("Options")}
					aria-label={trUI("Battle options")}
				>
					{trUI("Battle options")} <i class="fa fa-caret-down" aria-hidden></i>
				</button>
				<ChatLog
					class="battle-log"
					room={room} left={chatLeft} top={0} bottom={0} noSubscription hasPreempt
				>
					{}
				</ChatLog>
				{/* Compose under arena/controls — between userlist and right log */}
				<ChatTextEntry
					room={room} onMessage={this.send} onKey={this.onKey}
					left={userListWidth} right={chatWidth} className="et-spec-compose"
					tinyLayout={battleColumnWidth < 400}
				/>
			</PSPanelWrapper>;
		}

		// ── Player classic side-by-side (pre-ulist structure) ──
		// [battle + move controls, left-aligned] [battle log / chat fills to right edge]
		const stagePadX = 8;
		const chatGap = 8;
		const chatWidth = this.playerChatFillWidth(battleWidth, stagePadX, chatGap);
		const leftColWidth = Math.max(battleWidth + stagePadX, room.width - chatWidth);
		const chatLeft = room.width - chatWidth;
		return <PSPanelWrapper room={room} focusClick noScroll="hidden" className="et-player-battle">
			{hardcoreStyle}
			<style dangerouslySetInnerHTML={{
				__html: `#${id}.et-player-battle{--et-player-chat-w:${chatWidth}px}`,
			}} />
			<div class="scrollable-battle-container et-battle-stage" style={`width:${leftColWidth}px`}>
				<div
					class="et-player-stage-inner"
					style={`position:relative;width:${battleWidth}px;height:${battleHeight}px;margin:0;margin-left:${stagePadX}px;`}
				>
					<BattleDiv room={room} />
					{overlayVersion && <div
						class="overlay-controls"
						style={`position:absolute;left:0;top:${battleHeight}px;width:${battleWidth}px;height:0`}
					>
						{this.renderControls(true)}
					</div>}
				</div>
				<div class="battle-controls-container">
					<div
						class={`battle-controls${battleWidth >= 639 ? ' wide-controls' : ''}`}
						role="complementary" aria-label={trUI("Battle Controls")}
						style={`top:${battleHeight + 10}px;left:${stagePadX}px;width:${battleWidth}px;`}
					>
						{(room.battle && !room.battle.ended && room.request && room.battle.mySide.id === PS.user.userid) &&
							<TimerButton room={room} top={0} />}
						{this.renderControls(false, overlayVersion)}
						{this.renderConnectError()}
					</div>
				</div>
			</div>
			<ChatLog
				class="battle-log hasuserlist" room={room} left={chatLeft} noSubscription hasPreempt
			>
				{}
			</ChatLog>
			<ChatTextEntry
				room={room} onMessage={this.send} onKey={this.onKey} left={chatLeft}
				tinyLayout={chatWidth < 300}
			/>
			<ChatUserList room={room} left={chatLeft} minimized />
		</PSPanelWrapper>;
	}
}

PS.addRoomType(BattlePanel, BattlesPanel);
